import _ from 'lodash';

import { CIC_SAVE_NOTE_MAX_LENGTH } from 'constants/cicReason';
import { getReasonsForFee } from 'utils/cicReason';

import {
  COMMENT_REQUIRED_MESSAGE,
  EMPTY_FEE_ROW_VALUE,
  EMPTY_ROW_VALUE,
  FEE_REASON_REQUIRED_MESSAGE,
  NOTE_REQUIRED_MESSAGE,
  REASON_REQUIRED_MESSAGE,
} from './CicFieldReasonsForm.constants';

export const getCommentError = comment => {
  const trimmed = (comment ?? '').trim();

  if (!trimmed) {
    return COMMENT_REQUIRED_MESSAGE;
  }

  if (trimmed.length > CIC_SAVE_NOTE_MAX_LENGTH) {
    return `Comment must be ${CIC_SAVE_NOTE_MAX_LENGTH} characters or fewer`;
  }

  return '';
};

export const getReasonError = cicReasonId => (cicReasonId ? '' : REASON_REQUIRED_MESSAGE);

const getIsRowComplete = row =>
  !!row && !getReasonError(row.cicReasonId) && !getCommentError(row.comment);

export const getRowValue = (rowValues, field) => rowValues[field] ?? EMPTY_ROW_VALUE;

export const getCompletedRowsCount = (changes, rowValues) =>
  changes.filter(({ field }) => getIsRowComplete(rowValues[field])).length;

export const getInitialRowValues = (changes, previousCicReasons, reasons) => {
  const activeReasonIds = new Set(reasons.map(({ cicFeeReasonId }) => cicFeeReasonId));
  const previousByField = _.keyBy(previousCicReasons, 'field');

  return changes.reduce((acc, { field }) => {
    const previous = previousByField[field];

    acc[field] = {
      cicReasonId: activeReasonIds.has(previous?.cicReasonId) ? previous.cicReasonId : null,
      comment: previous?.comment ?? '',
    };

    return acc;
  }, {});
};

export const copyFirstRowToAll = (changes, rowValues) => {
  const firstRow = getRowValue(rowValues, changes[0]?.field);

  return changes.reduce((acc, { field }) => {
    acc[field] = { ...firstRow };

    return acc;
  }, {});
};

export const buildCicReasons = (changes, rowValues) =>
  changes.map(({ field }) => ({
    field,
    cicReasonId: rowValues[field].cicReasonId,
    comment: rowValues[field].comment.trim(),
  }));

export const getRecalculatedFieldKey = ({ label, oldDisplay, newDisplay }) =>
  `${label}:${oldDisplay ?? ''}:${newDisplay ?? ''}`;

export const getFeeRowValue = (feeRowValues, feeTypeId) =>
  feeRowValues[feeTypeId] ?? EMPTY_FEE_ROW_VALUE;

export const getFeeReasonError = row =>
  !row.isCure && !row.cicReasonId ? FEE_REASON_REQUIRED_MESSAGE : '';

export const getFeeNoteError = row => {
  const trimmedNote = row.note.trim();

  if (row.isCure) {
    return '';
  }

  if (row.cicReasonId && !trimmedNote) {
    return NOTE_REQUIRED_MESSAGE;
  }

  return trimmedNote.length > CIC_SAVE_NOTE_MAX_LENGTH
    ? `Notes must be ${CIC_SAVE_NOTE_MAX_LENGTH} characters or fewer`
    : '';
};

export const getCompletedFeeRowsCount = (feeChanges, feeRowValues) =>
  feeChanges.filter(({ feeTypeId }) => {
    const row = getFeeRowValue(feeRowValues, feeTypeId);

    return !getFeeReasonError(row) && !getFeeNoteError(row);
  }).length;

export const getInitialFeeRowValues = (feeChanges, previousCicFeeReasons, reasons) => {
  const previousByFeeType = _.keyBy(previousCicFeeReasons, 'feeTypeId');

  return feeChanges.reduce((acc, { feeTypeId, currentReasonId, currentNote }) => {
    const previous = previousByFeeType[feeTypeId];
    // A previous attempt wins; otherwise a reason the user already set for this fee (e.g. on the Fees page) is offered
    // pre-filled to verify. The server never sends an automatic reason here, so a fee without one starts blank.
    const source = previous ?? { cicReasonId: currentReasonId, note: currentNote };
    const isReasonAvailable = getReasonsForFee(reasons, feeTypeId).some(
      ({ cicFeeReasonId }) => cicFeeReasonId === source.cicReasonId,
    );

    acc[feeTypeId] = {
      cicReasonId: isReasonAvailable ? source.cicReasonId : null,
      note: source.note ?? '',
      isCure: !!previous?.isCure,
    };

    return acc;
  }, {});
};

export const buildCicFeeReasons = (feeChanges, feeRowValues) =>
  feeChanges.map(({ feeTypeId, newAmount }) => {
    const { cicReasonId, note, isCure } = getFeeRowValue(feeRowValues, feeTypeId);

    return isCure
      ? { feeTypeId, cicReasonId: null, note: null, isCure, newAmount }
      : { feeTypeId, cicReasonId, note: note.trim() || null, isCure, newAmount };
  });

export const getProgressLabel = ({ completedCount, totalCount, hasFeeChanges }) =>
  `${completedCount} of ${totalCount} ${hasFeeChanges ? 'items' : 'fields'} complete`;
